import _uniqBy from 'lodash/uniqBy';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

/**
 * Filters an array into unique values based off of a property of the objects in the array
 */
export function uniqBy<T extends {}>(array: T[], property: keyof T) {
  return _uniqBy(array, property);
}
/**
 * Returns a new array unlike `[].prototype.sort` which sorts in place.
 */
export function sort<T>(arr: T[], compareFn?: (a: T, b: T) => number) {
  return [...arr].sort(compareFn);
}

export function keys<T>(o: T) {
  return Object.keys(o) as (keyof T)[];
}

export const isFirst = <T>(arr: T[], idx: number) => arr[0] === arr[idx];

export const isLast = <T>(arr: T[], idx: number) => arr[idx] === arr[arr.length - 1];

export const partition = <T>(arr: T[], test: (item: T) => boolean) => {
  const pass = [];
  const fail = [];

  for (const item of arr) {
    if (test(item)) {
      pass.push(item);
    } else {
      fail.push(item);
    }
  }

  return [pass, fail] as const;
};

/**
 * Inserts an item at the specified index, pushing the existing item down.
 */
export const insertAtIndex = <T>(arr: T[], item: T | T[], index: number): T[] => {
  // If the index is greater than the length of the array, just insert at the end
  if (index > arr.length - 1) {
    return arr.concat(item);
  }

  return [...arr.slice(0, index).concat(item), ...arr.slice(index)];
};

/**
 * Creates an array of `count` values indexed from 0 to count - 1
 */
export const range = (count: number) => Array.from({ length: count }, (v, i) => i);

export function hasLength(array: any): array is Array<any> {
  return Array.isArray(array) && !!array.length;
}

/**
 * Check if value is either empty, null or undefined
 * @param value any
 * @returns boolean
 */
export const isNada = (value: any) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return !value;
  }
  if (isEmpty(value)) return true;
  if (isNil(value)) return true;
  return false;
};
